import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useEffect, useMemo, useState } from 'react'
import { Check, Flame, Loader2, MessageSquarePlus, FilePlus2, Ticket, X, Zap } from 'lucide-react'
import { toast } from 'sonner'
import { AppHeader } from '#/components/layout/app-header'
import { Button } from '#/components/ui/button'
import { Badge } from '#/components/ui/badge'
import { Input } from '#/components/ui/input'
import { ApiError, usePricing, useSubscription, type PricingPlanDto } from '#/lib/api'
import {
  applyVoucher,
  createTransaction,
  payWithPaddle,
  payWithPakasir,
  previewDiscount,
  useAutoVoucher,
  useInvalidateBilling,
  usePaymentsConfig,
  verifyTransaction,
  voucherAppliesTo,
  type AddonKind,
  type CreateTransactionResult,
  type Currency,
  type VoucherDto,
  type VoucherPricing,
} from '#/lib/api-billing'
import { authClient } from '#/lib/auth-client'
import { fmtCurrency, fmtPriceShort } from '#/lib/format'
import { cn } from '#/lib/utils'
import { m } from '#/paraglide/messages'
import { getLocale } from '#/paraglide/runtime'

export const Route = createFileRoute('/pricing')({
  component: Pricing,
  validateSearch: (s: Record<string, unknown>): { voucher?: string } => ({ voucher: typeof s.voucher === 'string' && s.voucher ? s.voucher : undefined }),
})

const voucherValue = (v: Pick<VoucherDto, 'kind' | 'value'>) => (v.kind === 'percent' ? `${v.value}%` : fmtCurrency(v.value, 'IDR'))
const voucherError = (e: unknown) => {
  const code = e instanceof ApiError ? e.code : undefined
  if (code === 'voucher_currency') return m.billing_voucher_currency()
  if (code === 'voucher_not_applicable') return m.billing_voucher_not_applicable()
  if (code === 'invalid_voucher') return m.billing_voucher_invalid()
  return e instanceof ApiError ? e.message : m.common_error_generic()
}

function Pricing() {
  const { voucher: voucherFromUrl } = Route.useSearch()
  const navigate = useNavigate()
  const { data } = usePricing()
  const { data: cfg } = usePaymentsConfig()
  const { data: session } = authClient.useSession()
  const { data: sub } = useSubscription(Boolean(session?.user))
  const { data: auto } = useAutoVoucher()
  const invalidate = useInvalidateBilling()
  const [currency, setCurrency] = useState<Currency>(getLocale() === 'id' ? 'IDR' : 'USD')
  const [months, setMonths] = useState<1 | 3>(1)
  const [code, setCode] = useState(voucherFromUrl ?? '')
  const [voucher, setVoucher] = useState<VoucherDto | null>(null)
  const [previews, setPreviews] = useState<Record<string, VoucherPricing>>({})
  const [busy, setBusy] = useState<string | null>(null)
  const [pendingSim, setPendingSim] = useState<CreateTransactionResult | null>(null)

  const plans = data?.plans ?? []
  const byCur = plans.filter((p) => p.currency === currency && p.price > 0)
  const fablePlan = byCur.find((p) => p.slug.replace(/-usd$/, '') === 'fable')
  // Facilitator tier (PLAN_DESIGN_ROOM_TIM.md F4): coaches / capstone lecturers / agencies — monthly plan only in this teaser.
  const facilitatorPlan = byCur.find((p) => p.slug.replace(/-usd$/, '') === 'facilitator')
  const pick = (base: 'starter' | 'pro', days: number) => byCur.find((p) => p.slug.replace(/-usd$/, '').replace(/-\d+mo$/, '') === base && p.durationDays === days)
  const cards = useMemo(() => {
    const monthly = { starter: pick('starter', 30), pro: pick('pro', 30) }
    const list: { plan: PricingPlanDto; monthlyRef?: PricingPlanDto; best?: boolean }[] = []
    if (months === 1) {
      if (monthly.starter) list.push({ plan: monthly.starter })
      if (monthly.pro) list.push({ plan: monthly.pro })
    } else {
      const s3 = pick('starter', 90), p3 = pick('pro', 90)
      if (s3) list.push({ plan: s3, monthlyRef: monthly.starter })
      if (p3) list.push({ plan: p3, monthlyRef: monthly.pro })
    }
    const yearly = pick('pro', 365)
    if (yearly) list.push({ plan: yearly, monthlyRef: monthly.pro, best: true })
    return list
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [byCur, months])

  // ---- voucher: validate once, then preview the discounted total per visible card via /api/vouchers/apply {code, planSlug}
  const apply = async (c = code) => {
    const trimmed = c.trim().toUpperCase()
    if (!trimmed) return
    if (!session?.user) {
      navigate({ to: '/sign-in', search: { redirect: `/pricing?voucher=${encodeURIComponent(trimmed)}` } as never })
      return
    }
    try {
      const r = await applyVoucher(trimmed)
      setVoucher(r.voucher)
      setCode(trimmed)
      toast.success(m.billing_voucher_applied({ code: r.voucher.code, value: voucherValue(r.voucher) }))
    } catch (e) {
      setVoucher(null)
      setPreviews({})
      toast.error(voucherError(e))
    }
  }
  useEffect(() => {
    if (voucherFromUrl && session?.user && !voucher) void apply(voucherFromUrl)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voucherFromUrl, session?.user])
  useEffect(() => {
    if (!voucher) return
    let alive = true
    const slugs = cards.map((c) => c.plan.slug).filter((s) => !previews[s])
    if (!slugs.length) return
    void Promise.all(slugs.map((s) => applyVoucher(voucher.code, s).then((r) => [s, r.pricing] as const).catch(() => [s, null] as const))).then((rows) => {
      if (!alive) return
      setPreviews((prev) => {
        const next = { ...prev }
        for (const [s, p] of rows) if (p) next[s] = p
        return next
      })
    })
    return () => {
      alive = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voucher, cards])
  const clearVoucher = () => {
    setVoucher(null)
    setPreviews({})
    setCode('')
  }
  /** Discounted total for a card: explicit voucher (server preview) → auto promo (client preview, IDR only). */
  const discountFor = (p: PricingPlanDto): number => {
    if (voucher) return previews[p.slug]?.discount ?? 0
    if (auto?.voucher && p.currency === 'IDR' && voucherAppliesTo(auto.voucher, p)) return previewDiscount(p.price, auto.voucher)
    return 0
  }

  // ---- checkout
  const finish = async (orderId: string, extra: { providerRef?: string; simulate?: 'settlement' } = {}) => {
    const r = await verifyTransaction({ orderId, ...extra })
    invalidate()
    if (r.status === 'paid') {
      toast.success(m.billing_paid_success())
      setPendingSim(null)
      navigate({ to: '/settings' })
    } else if (r.status === 'pending') toast.message(m.billing_paid_pending())
    else toast.error(m.billing_paid_error())
    return r
  }
  const checkout = async (r: CreateTransactionResult) => {
    if (r.status === 'paid') {
      invalidate()
      toast.success(m.billing_free_activated())
      navigate({ to: '/settings' })
      return
    }
    if (!r.configured || !cfg) {
      toast.message(m.billing_gateway_not_configured())
      if (r.simulate) setPendingSim(r)
      return
    }
    // Pakasir is a hosted page: leave the app and come back on /transactions?order=… (which re-verifies).
    if (r.provider === 'pakasir' && r.checkoutUrl) {
      toast.message(m.billing_redirecting())
      payWithPakasir(r.checkoutUrl)
      return
    }
    await payWithPaddle(cfg.paddle, r, {
      email: session?.user?.email,
      onCompleted: (transactionId) => void finish(r.orderId, { providerRef: transactionId }),
    })
  }
  const buy = async (body: { planSlug?: string; addon?: AddonKind }, key: string) => {
    if (!session?.user) {
      window.location.href = '/sign-in?redirect=/pricing'
      return
    }
    setBusy(key)
    try {
      const r = await createTransaction({ ...body, currency, voucherCode: voucher?.code })
      await checkout(r)
    } catch (e) {
      toast.error(e instanceof ApiError ? voucherError(e) : m.common_error_generic())
    } finally {
      setBusy(null)
    }
  }
  const simulate = async () => {
    if (!pendingSim) return
    setBusy('sim')
    try {
      await finish(pendingSim.orderId, { simulate: 'settlement' })
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : m.common_error_generic())
    } finally {
      setBusy(null)
    }
  }
  const addons = cfg?.addons

  return (
    <>
      <AppHeader />
      <main className="flex-1 overflow-y-auto px-6 py-10">
        <div className="mx-auto w-full max-w-5xl space-y-8">
          <div className="flex flex-col items-center gap-3 text-center">
            <span className="inline-flex items-center gap-1 rounded-full border bg-card px-3 py-1 text-xs font-medium text-muted-foreground"><Flame className="size-3.5 text-primary" /> {m.pricing_promo()}</span>
            <h1 className="text-4xl font-bold">{m.pricing_title()}</h1>
            <p className="text-muted-foreground">{m.pricing_subtitle()}</p>
            {auto?.voucher && !voucher ? (
              <Badge variant="secondary" className="gap-1 font-normal"><Ticket className="size-3.5" /> {m.billing_auto_promo({ code: auto.voucher.code, value: voucherValue(auto.voucher) })}</Badge>
            ) : null}
            <form
              className="flex w-full max-w-sm items-center gap-2"
              onSubmit={(e) => {
                e.preventDefault()
                void apply()
              }}
            >
              <div className="relative flex-1">
                <Ticket className="pointer-events-none absolute top-1/2 left-2.5 size-4 -translate-y-1/2 text-muted-foreground" />
                <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder={m.billing_voucher_label()} aria-label={m.pricing_voucher()} className="pl-8 uppercase" disabled={Boolean(voucher)} />
              </div>
              {voucher ? (
                <Button type="button" variant="secondary" onClick={clearVoucher}><X className="size-4" /> {m.billing_voucher_remove()}</Button>
              ) : (
                <Button type="submit" variant="secondary" disabled={!code.trim()}>{m.billing_voucher_apply()}</Button>
              )}
            </form>
            {voucher ? <p className="text-xs text-status-done">{m.billing_voucher_applied({ code: voucher.code, value: voucherValue(voucher) })}</p> : null}
          </div>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <div className="flex overflow-hidden rounded-full border bg-card p-1 text-sm">
              <button className={cn('rounded-full px-4 py-1.5', months === 1 && 'bg-primary text-primary-foreground')} onClick={() => setMonths(1)}>{m.pricing_1mo()}</button>
              <button className={cn('flex items-center gap-1.5 rounded-full px-4 py-1.5', months === 3 && 'bg-primary text-primary-foreground')} onClick={() => setMonths(3)}>{m.pricing_3mo()} <Badge variant="secondary" className="h-4 px-1.5 text-[10px]">{m.pricing_save()}</Badge></button>
            </div>
            <div className="flex overflow-hidden rounded-full border bg-card p-1 text-sm" aria-label={m.pricing_currency()}>
              {(['IDR', 'USD'] as const).map((c) => (
                <button key={c} className={cn('rounded-full px-4 py-1.5', currency === c && 'bg-secondary')} onClick={() => setCurrency(c)}>{c}</button>
              ))}
            </div>
          </div>
          {pendingSim ? (
            <div className="mx-auto flex max-w-2xl flex-wrap items-center justify-between gap-3 rounded-xl border border-dashed border-primary/60 bg-primary/5 px-4 py-3 text-sm">
              <div>
                <div className="font-medium">{m.billing_simulate_hint()}</div>
                <div className="font-mono text-xs text-muted-foreground">{pendingSim.orderId} · {fmtCurrency(pendingSim.amount, pendingSim.currency)}</div>
              </div>
              <Button size="sm" onClick={simulate} disabled={busy === 'sim'}>{busy === 'sim' ? <Loader2 className="size-4 animate-spin" /> : null} {m.billing_simulate()}</Button>
            </div>
          ) : null}
          <div className="grid gap-4 md:grid-cols-3">
            {cards.map(({ plan, monthlyRef, best }) => {
              const monthsIn = Math.max(1, Math.round(plan.durationDays / 30))
              const perMonth = plan.price / monthsIn
              const ref = monthlyRef ? monthlyRef.price * monthsIn : null
              const off = ref && ref > plan.price ? Math.round((1 - plan.price / ref) * 100) : 0
              const active = sub?.planType === plan.slug
              const baseName = plan.slug.replace(/-usd$/, '').replace(/-\d+mo$/, '')
              const discount = discountFor(plan)
              const loading = busy === plan.slug
              return (
                <div key={plan.id} className={cn('relative flex flex-col rounded-2xl border bg-card p-6', best && 'border-primary shadow-md')}>
                  {best ? <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground"><Zap className="mr-1 inline size-3" />{m.pricing_best()}</span> : null}
                  <div className="flex items-center justify-between">
                    <h2 className="font-heading text-lg font-semibold">{plan.name}</h2>
                    <div className="flex gap-1">
                      {active ? <Badge>{m.pricing_current()}</Badge> : null}
                      {off ? <Badge variant="secondary">{m.pricing_off({ pct: off })}</Badge> : null}
                    </div>
                  </div>
                  <p className="text-sm text-muted-foreground">{plan.description}</p>
                  <div className="mt-4">
                    <span className="text-3xl font-bold">{fmtPriceShort(perMonth, plan.currency)}</span>
                    <span className="text-sm text-muted-foreground"> {m.pricing_per_month()}</span>
                  </div>
                  <div className="text-xs text-muted-foreground">
                    {m.pricing_total()} {ref && ref > plan.price ? <s className="mr-1">{fmtCurrency(ref, plan.currency)}</s> : null}
                    <span className="font-medium text-foreground">{fmtCurrency(plan.price, plan.currency)}</span> {plan.durationDays >= 365 ? m.pricing_per_year() : plan.durationDays >= 90 ? m.pricing_per_3mo() : m.pricing_per_month()}
                  </div>
                  {discount > 0 ? (
                    <div className="mt-1 inline-flex items-center gap-1 self-start rounded-md bg-status-done/10 px-2 py-0.5 text-xs text-status-done">
                      <Ticket className="size-3" /> <span className="font-semibold">{fmtCurrency(plan.price - discount, plan.currency)}</span> {m.billing_with_voucher()}
                    </div>
                  ) : null}
                  <ul className="mt-4 flex-1 space-y-2 text-sm">
                    {plan.features.map((f) => <li key={f} className="flex gap-2"><Check className="mt-0.5 size-4 shrink-0 text-status-done" /> {f}</li>)}
                  </ul>
                  <Button className="mt-6 h-11" variant={best || active ? 'default' : 'secondary'} disabled={Boolean(busy)} onClick={() => buy({ planSlug: plan.slug }, plan.slug)}>
                    {loading ? <Loader2 className="size-4 animate-spin" /> : null}
                    {!session?.user ? m.billing_signin_first() : active ? m.pricing_extend({ duration: monthsIn === 12 ? m.pricing_per_year().replace('/', '') : monthsIn === 3 ? m.pricing_3mo() : m.pricing_1mo() }) : m.pricing_choose({ plan: baseName === 'pro' ? 'Pro' : 'Starter' })}
                  </Button>
                </div>
              )
            })}
          </div>
          {addons ? (
            <section className="mx-auto max-w-3xl rounded-2xl border bg-card/60 p-5">
              <div className="flex flex-col gap-1 sm:flex-row sm:items-baseline sm:justify-between">
                <h2 className="font-heading text-base font-semibold">{m.billing_addons_title()}</h2>
                <p className="text-xs text-muted-foreground">{m.billing_addons_desc()}</p>
              </div>
              <div className="mt-3 grid gap-3 sm:grid-cols-2">
                {(['prd', 'chat'] as const).map((kind) => {
                  const a = addons[kind][currency]
                  const loading = busy === `addon-${kind}`
                  return (
                    <div key={kind} className="flex items-center justify-between gap-3 rounded-xl border bg-card px-4 py-3">
                      <div className="flex items-center gap-2 text-sm font-medium">
                        {kind === 'prd' ? <FilePlus2 className="size-4 text-primary" /> : <MessageSquarePlus className="size-4 text-primary" />}
                        {kind === 'prd' ? m.billing_addon_prd({ n: a.credits }) : m.billing_addon_chat({ n: a.credits })}
                      </div>
                      <Button size="sm" variant="outline" disabled={Boolean(busy)} onClick={() => buy({ addon: kind }, `addon-${kind}`)}>
                        {loading ? <Loader2 className="size-4 animate-spin" /> : null} {m.billing_addon_buy({ price: fmtCurrency(a.price, currency) })}
                      </Button>
                    </div>
                  )
                })}
              </div>
            </section>
          ) : null}
          {/* Fable plan (model selector, owner decision 4 Sep 2026): dedicated package for Claude Fable 5. */}
          {fablePlan ? (
            <section id="fable" className="mx-auto flex max-w-3xl scroll-mt-20 flex-col gap-3 rounded-2xl border border-primary/40 bg-card p-5 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <div className="flex items-baseline gap-2">
                  <h2 className="font-heading text-base font-semibold">{m.model_fable_plan_title()}</h2>
                  <span className="text-sm font-semibold">{fmtCurrency(fablePlan.price, fablePlan.currency)} {m.pricing_per_month()}</span>
                </div>
                <p className="mt-0.5 text-sm text-muted-foreground">{m.model_fable_plan_desc()}</p>
                <ul className="mt-2 grid gap-1 text-sm sm:grid-cols-2">
                  {[m.model_fable_plan_feat1(), m.model_fable_plan_feat2(), m.model_fable_plan_feat3(), m.model_fable_plan_feat4()].map((f) => (
                    <li key={f} className="flex gap-2"><Check className="mt-0.5 size-4 shrink-0 text-status-done" /> {f}</li>
                  ))}
                </ul>
              </div>
              <Button className="shrink-0" disabled={Boolean(busy)} onClick={() => buy({ planSlug: fablePlan.slug }, fablePlan.slug)}>{sub?.planType === fablePlan.slug ? m.pricing_current() : m.model_fable_plan_cta()}</Button>
            </section>
          ) : null}
          {/* Facilitator tier (PLAN_DESIGN_ROOM_TIM.md F4 / RISET_DESIGN_ROOM_TIM.md §5.3): design-thinking coaches, capstone lecturers, agencies. */}
          {facilitatorPlan ? (
            <section id="facilitator" className="mx-auto flex max-w-3xl scroll-mt-20 flex-col gap-3 rounded-2xl border border-primary/40 bg-card p-5 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <div className="flex items-baseline gap-2">
                  <h2 className="font-heading text-base font-semibold">{m.fac_plan_title()}</h2>
                  <span className="text-sm font-semibold">{fmtCurrency(facilitatorPlan.price, facilitatorPlan.currency)} {m.pricing_per_month()}</span>
                </div>
                <p className="mt-0.5 text-sm text-muted-foreground">{m.fac_plan_desc()}</p>
                <ul className="mt-2 grid gap-1 text-sm sm:grid-cols-2">
                  {[m.fac_plan_feat1(), m.fac_plan_feat2(), m.fac_plan_feat3(), m.fac_plan_feat4()].map((f) => (
                    <li key={f} className="flex gap-2"><Check className="mt-0.5 size-4 shrink-0 text-status-done" /> {f}</li>
                  ))}
                </ul>
                <p className="mt-2 text-xs text-muted-foreground">{m.fac_callout()} <a href="/design-room" className="text-primary hover:underline">{m.fac_callout_link()}</a></p>
              </div>
              <Button className="shrink-0" disabled={Boolean(busy)} onClick={() => buy({ planSlug: facilitatorPlan.slug }, facilitatorPlan.slug)}>{sub?.planType === facilitatorPlan.slug ? m.pricing_current() : m.fac_plan_cta()}</Button>
            </section>
          ) : null}
          {/* Studio tier (moat plan §5.3) — teaser only; the tier itself is sold per workspace, not through this checkout yet. */}
          <section id="studio" className="mx-auto flex max-w-3xl scroll-mt-20 flex-col gap-3 rounded-2xl border bg-card p-5 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <div className="flex items-baseline gap-2">
                <h2 className="font-heading text-base font-semibold">{m.st_pricing_title()}</h2>
                <span className="text-sm font-semibold">{m.st_pricing_price()}</span>
              </div>
              <p className="mt-0.5 text-sm text-muted-foreground">{m.st_pricing_desc()}</p>
              <ul className="mt-2 grid gap-1 text-sm sm:grid-cols-2">
                {[m.st_pricing_feat1(), m.st_pricing_feat2(), m.st_pricing_feat3(), m.st_pricing_feat4()].map((f) => (
                  <li key={f} className="flex gap-2"><Check className="mt-0.5 size-4 shrink-0 text-status-done" /> {f}</li>
                ))}
              </ul>
            </div>
            <Button asChild variant="secondary" className="shrink-0"><Link to="/studio">{m.st_pricing_cta()}</Link></Button>
          </section>
          {/* Campus program (docs/campus) — per team per semester; classes & campus partners are invoiced from the /campus form. */}
          <section id="campus" className="mx-auto flex max-w-3xl scroll-mt-20 flex-col gap-3 rounded-2xl border bg-card p-5 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <div className="flex items-baseline gap-2">
                <h2 className="font-heading text-base font-semibold">{m.cp_pricing_title()}</h2>
                <span className="text-sm font-semibold">{m.cp_pricing_price()}</span>
              </div>
              <p className="mt-0.5 text-sm text-muted-foreground">{m.cp_pricing_desc()}</p>
              <ul className="mt-2 grid gap-1 text-sm sm:grid-cols-2">
                {[m.cp_pricing_feat1(), m.cp_pricing_feat2(), m.cp_pricing_feat3(), m.cp_pricing_feat4()].map((f) => (
                  <li key={f} className="flex gap-2"><Check className="mt-0.5 size-4 shrink-0 text-status-done" /> {f}</li>
                ))}
              </ul>
            </div>
            <Button asChild variant="secondary" className="shrink-0"><Link to="/campus">{m.cp_pricing_cta()}</Link></Button>
          </section>
          <p className="text-center text-xs text-muted-foreground">
            {m.pricing_footer()} {currency === 'USD' ? m.billing_paddle_note() : m.billing_pakasir_note()} <Link to="/vouchers" className="text-primary hover:underline">{m.pricing_voucher()}</Link>
          </p>
        </div>
      </main>
    </>
  )
}
